import type { CallEndReason, CallMedia, IceServersDto, ServerEvent, UserSummaryDto } from '@dijitalapp/shared';
import { api } from './api';
import { uuidv7 } from './outbox';
import type { RealtimeClient } from './realtime';

/**
 * 1:1 WebRTC calls (ADR-015). The browser does all media and DTLS-SRTP encryption; this class only runs
 * the offer/answer exchange over the realtime gateway and keeps the UI state.
 */

export type CallPhase = 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';
export type CallFailure = CallEndReason | 'permission' | 'unsupported' | 'not_allowed';

export interface CallView {
  callId: string;
  conversationId: string;
  peer: Pick<UserSummaryDto, 'id' | 'displayName'>;
  media: CallMedia;
  phase: CallPhase;
  muted: boolean;
  cameraOff: boolean;
  /** Time the media connected (for the call timer). */
  connectedAt: number | null;
  endReason: CallFailure | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
}

export function callsSupported(): boolean {
  return typeof RTCPeerConnection !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

export class CallManager {
  view: CallView | null = null;
  private pc: RTCPeerConnection | null = null;
  private pendingIce: RTCIceCandidateInit[] = [];
  private incomingOffer: string | null = null;
  private listeners = new Set<() => void>();
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private ringer: { stop: () => void } | null = null;

  constructor(private readonly rt: RealtimeClient) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private update(patch: Partial<CallView>) {
    if (!this.view) return;
    this.view = { ...this.view, ...patch };
    this.listeners.forEach((l) => l());
  }

  private async connection(): Promise<RTCPeerConnection> {
    const ice = await api<IceServersDto>('GET', '/calls/ice-servers');
    const pc = new RTCPeerConnection({
      iceServers: ice.iceServers,
      // Relay-only hides each side's IP address from the other (media goes through TURN).
      iceTransportPolicy: ice.relayOnly ? 'relay' : 'all',
    });
    pc.onicecandidate = (e) => {
      if (e.candidate && this.view) this.rt.command('call.ice', { callId: this.view.callId, candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.update({ remoteStream: stream });
    };
    pc.onconnectionstatechange = () => {
      if (!this.view || this.pc !== pc) return;
      if (pc.connectionState === 'connected') this.update({ phase: 'active', connectedAt: this.view.connectedAt ?? Date.now() });
      if (pc.connectionState === 'failed') this.hangup('failed');
    };
    this.pc = pc;
    return pc;
  }

  private async media(video: boolean): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: video ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false });
  }

  private async flushIce() {
    const pc = this.pc;
    if (!pc?.remoteDescription) return;
    for (const c of this.pendingIce.splice(0)) await pc.addIceCandidate(c).catch(() => undefined);
  }

  private busy() {
    return this.view !== null && this.view.phase !== 'ended';
  }

  /** Places a call to the peer of a direct conversation. */
  async start(conversationId: string, peer: CallView['peer'], media: CallMedia): Promise<void> {
    if (this.busy()) return;
    const callId = uuidv7();
    this.view = { callId, conversationId, peer, media, phase: 'outgoing', muted: false, cameraOff: false, connectedAt: null, endReason: null, localStream: null, remoteStream: null };
    this.listeners.forEach((l) => l());
    if (!callsSupported()) return this.finish('unsupported');
    let stream: MediaStream;
    try {
      stream = await this.media(media === 'video');
    } catch {
      return this.finish('permission');
    }
    if (this.view?.callId !== callId) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.update({ localStream: stream });
    try {
      const pc = await this.connection();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.rt.command('call.invite', { callId, conversationId, media, sdp: offer.sdp ?? '' }, callId);
    } catch {
      this.finish('failed');
    }
  }

  /** Answers the ringing call; `withVideo` false answers a video call with audio only. */
  async accept(withVideo = true): Promise<void> {
    const view = this.view;
    if (!view || view.phase !== 'incoming' || !this.incomingOffer) return;
    this.stopRinging();
    this.update({ phase: 'connecting' });
    let stream: MediaStream;
    try {
      stream = await this.media(view.media === 'video' && withVideo);
    } catch {
      this.rt.command('call.reject', { callId: view.callId });
      return this.finish('permission');
    }
    this.update({ localStream: stream, cameraOff: view.media === 'video' && !withVideo });
    try {
      const pc = await this.connection();
      await pc.setRemoteDescription({ type: 'offer', sdp: this.incomingOffer });
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.rt.command('call.answer', { callId: view.callId, sdp: answer.sdp ?? '' }, view.callId);
      await this.flushIce();
    } catch {
      this.hangup('failed');
    }
  }

  reject(): void {
    if (!this.view) return;
    this.rt.command('call.reject', { callId: this.view.callId });
    this.finish('rejected');
  }

  hangup(reason: 'hangup' | 'failed' = 'hangup'): void {
    if (!this.view || this.view.phase === 'ended') return;
    this.rt.command('call.hangup', { callId: this.view.callId, reason });
    this.finish(reason === 'failed' ? 'failed' : this.view.phase === 'outgoing' ? 'cancelled' : 'hangup');
  }

  toggleMute(): void {
    const muted = !this.view?.muted;
    this.view?.localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
    this.update({ muted });
  }

  toggleCamera(): void {
    const cameraOff = !this.view?.cameraOff;
    this.view?.localStream?.getVideoTracks().forEach((t) => (t.enabled = !cameraOff));
    this.update({ cameraOff });
  }

  dismiss(): void {
    if (this.view?.phase === 'ended') {
      this.view = null;
      this.listeners.forEach((l) => l());
    }
  }

  private finish(reason: CallFailure) {
    this.stopRinging();
    this.pc?.close();
    this.pc = null;
    this.pendingIce = [];
    this.incomingOffer = null;
    this.view?.localStream?.getTracks().forEach((t) => t.stop());
    this.update({ phase: 'ended', endReason: reason, localStream: null, remoteStream: null });
    if (this.clearTimer) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => this.dismiss(), 4000);
  }

  private startRinging() {
    try {
      const ctx = new AudioContext();
      const gain = ctx.createGain();
      gain.gain.value = 0.08;
      gain.connect(ctx.destination);
      const beep = () => {
        const osc = ctx.createOscillator();
        osc.frequency.value = 660;
        osc.connect(gain);
        osc.start();
        osc.stop(ctx.currentTime + 0.4);
      };
      beep();
      const timer = setInterval(beep, 1500);
      this.ringer = { stop: () => (clearInterval(timer), void ctx.close()) };
    } catch {
      this.ringer = null; // Autoplay may be blocked until the user interacts with the page.
    }
  }

  private stopRinging() {
    this.ringer?.stop();
    this.ringer = null;
  }

  /** Server events routed here by the chat store. */
  handle(e: ServerEvent): void {
    switch (e.event) {
      case 'call.incoming': {
        // Already in a call on this device: the server marks the newcomer busy on its own only when the
        // user is busy everywhere, so decline it here.
        if (this.busy()) {
          this.rt.command('call.reject', { callId: e.payload.callId });
          return;
        }
        this.incomingOffer = e.payload.sdp;
        this.view = {
          callId: e.payload.callId,
          conversationId: e.payload.conversationId,
          peer: { id: e.payload.caller.id, displayName: e.payload.caller.displayName },
          media: e.payload.media,
          phase: 'incoming',
          muted: false,
          cameraOff: false,
          connectedAt: null,
          endReason: null,
          localStream: null,
          remoteStream: null,
        };
        this.listeners.forEach((l) => l());
        this.startRinging();
        return;
      }
      case 'call.answered':
        if (this.view?.callId === e.payload.callId && this.view.phase === 'outgoing' && this.pc) {
          this.update({ phase: 'connecting' });
          void this.pc
            .setRemoteDescription({ type: 'answer', sdp: e.payload.sdp })
            .then(() => this.flushIce())
            .catch(() => this.hangup('failed'));
        }
        return;
      case 'call.ice':
        if (this.view?.callId !== e.payload.callId) return;
        if (this.pc?.remoteDescription) void this.pc.addIceCandidate(e.payload.candidate).catch(() => undefined);
        else this.pendingIce.push(e.payload.candidate);
        return;
      case 'call.ended':
        if (this.view?.callId !== e.payload.callId || this.view.phase === 'ended') return;
        // This device answered: the "answered elsewhere" note is for the user's other devices.
        if (e.payload.reason === 'answered_elsewhere' && this.view.phase !== 'incoming') return;
        this.finish(e.payload.reason);
        return;
      case 'error':
        if (this.view && e.payload.reqId === this.view.callId && this.view.phase !== 'ended') {
          this.rt.command('call.hangup', { callId: this.view.callId });
          this.finish(e.payload.code === 'MESSAGING_NOT_ALLOWED' || e.payload.code === 'FORBIDDEN' ? 'not_allowed' : 'failed');
        }
        return;
      default:
        return;
    }
  }
}
